import {
  Component,
  computed,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import {
  selectShowWarningBanner,
  selectEnvironmentType,
  selectEnvironmentLabel,
  selectEnvironmentInfo,
} from '@core/state/environment';
import { DatabaseDisplayNamePipe } from '@shared/pipes/database-display-name/database-display-name.pipe';
import { SignalRStore } from '@core/services/signal-r/signal-r.service';
import { AppConfigService } from '@core/services/config/app-config.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { getPreviewPullRequestMetadata } from '../environment-warning-dialog/preview-pull-request-metadata';
import { MatExpIconButton } from '@camaradesuk/m3e-angular';

/**
 * Banner component that displays a warning strip for non-production environments.
 * Shows environment name and database info to prevent accidental production data changes.
 */
@Component({
  selector: 'app-environment-banner',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    DatabaseDisplayNamePipe,
    MatExpIconButton,
  ],
  template: `
    @if (showBanner()) {
      <div
        class="environment-banner"
        [class]="'environment-banner--' + environmentType()"
        role="alert"
      >
        <div class="environment-banner__content">
          <mat-icon class="environment-banner__icon">warning</mat-icon>
          <button
            class="environment-banner__info-button"
            (click)="infoClicked.emit()"
            aria-label="View environment details"
          >
            <strong>{{ environmentLabel() }}</strong>
            @if (previewPullRequestTitle(); as title) {
              <span class="environment-banner__pr-title" [title]="title">
                &bull; {{ title }}
              </span>
            }
            @if (info()?.databaseName; as db) {
              <span
                class="environment-banner__db"
                [class.environment-banner__db--production]="
                  isProductionDatabase()
                "
              >
                &bull; Database:
                {{
                  db
                    | databaseDisplayName
                      : $safeNavigationMigration(info()?.databaseType)
                }}
                @if (info()?.databaseLocation; as loc) {
                  <span class="environment-banner__location">({{ loc }})</span>
                }
                @if (isProductionDatabase()) {
                  <span class="environment-banner__persist-warning"
                    >(changes persist!)</span
                  >
                }
              </span>
            }
            <span
              class="environment-banner__signalr"
              [class.environment-banner__signalr--connected]="
                signalRConnected()
              "
              [class.environment-banner__signalr--disabled]="!signalREnabled()"
              [matTooltip]="signalRTooltip()"
            >
              <!-- No bullet separator here: the status dot already separates
                   this segment, and two dots side by side read as a glitch. -->
              <span class="environment-banner__signalr-dot">●</span>
              {{ signalRLabel() }}
            </span>
          </button>
        </div>
        @if (dismissible()) {
          <button
            mat-icon-button
            matExpIconButton
            class="environment-banner__close"
            (click)="dismissed.emit()"
            aria-label="Dismiss environment warning"
          >
            <mat-icon>close</mat-icon>
          </button>
        }
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .environment-banner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 16px;
        font-size: 14px;
        color: var(--syrf-on-env);
        z-index: 1000;
      }

      .environment-banner--staging {
        background: var(--syrf-env-staging);
      }

      .environment-banner--preview {
        background: var(--syrf-env-preview);
      }

      .environment-banner--development {
        background: var(--syrf-env-development);
      }

      .environment-banner--local {
        background: var(--syrf-env-local);
      }

      .environment-banner--production {
        display: none;
      }

      .environment-banner__content {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 1 1 auto;
        min-width: 0;
      }

      .environment-banner__icon {
        font-size: 20px;
        height: 20px;
        width: 20px;
      }

      .environment-banner__info-button {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
        background: none;
        border: none;
        color: inherit;
        font: inherit;
        cursor: pointer;
        padding: 4px 8px;
        margin: -4px -8px;
        border-radius: 4px;
        transition: background-color 0.15s ease;
        min-width: 0;
        max-width: 100%;
        text-align: left;
      }

      .environment-banner__info-button:hover {
        background-color: var(--syrf-on-env-hover);
      }

      .environment-banner__info-button:hover strong {
        text-decoration: underline;
      }

      .environment-banner__db {
        opacity: 0.9;
        font-size: 12px;
        overflow-wrap: anywhere;
      }

      .environment-banner__db--production {
        background: var(--syrf-env-prod-db-container);
        color: var(--syrf-on-env-prod-db);
        padding: 2px 8px;
        border-radius: 4px;
        font-weight: 600;
        opacity: 1;
      }

      .environment-banner__persist-warning {
        font-weight: 700;
        margin-left: 4px;
      }

      .environment-banner__close {
        color: var(--syrf-on-env);
        opacity: 0.8;
      }

      .environment-banner__close:hover {
        opacity: 1;
      }

      .environment-banner__signalr {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;
        opacity: 0.9;
        cursor: default;
      }

      .environment-banner__signalr-dot {
        font-size: 10px;
        color: var(--syrf-on-env-signal-disconnected);
      }

      .environment-banner__signalr--connected .environment-banner__signalr-dot {
        color: var(--syrf-on-env-signal-connected);
      }

      .environment-banner__signalr--disabled {
        opacity: 0.5;
      }

      .environment-banner__signalr--disabled .environment-banner__signalr-dot {
        color: var(--syrf-on-env-signal-off);
      }

      .environment-banner__pr-title {
        max-width: min(36rem, 45vw);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      @media (max-width: 600px) {
        .environment-banner {
          padding: 8px;
        }

        .environment-banner__icon {
          display: none;
        }

        .environment-banner__pr-title {
          max-width: 100%;
        }
      }
    `,
  ],
})
export class EnvironmentBannerComponent {
  private readonly store = inject(Store);
  private readonly signalRStore = inject(SignalRStore);
  private readonly appConfig = inject(AppConfigService);

  // Store selectors as signals
  protected readonly showBanner = this.store.selectSignal(
    selectShowWarningBanner
  );
  protected readonly environmentType = this.store.selectSignal(
    selectEnvironmentType
  );
  protected readonly environmentLabel = this.store.selectSignal(
    selectEnvironmentLabel
  );
  protected readonly info = this.store.selectSignal(selectEnvironmentInfo);

  // SignalR status
  protected readonly signalREnabled = computed(
    () => this.appConfig.config.signalRActive
  );
  protected readonly signalRConnected = toSignal(
    this.signalRStore.select((state) => state.connected),
    { initialValue: false }
  );

  protected readonly signalRLabel = computed(() => {
    if (!this.signalREnabled()) return 'SignalR off';
    return this.signalRConnected() ? 'SignalR' : 'SignalR disconnected';
  });

  protected readonly signalRTooltip = computed(() => {
    if (!this.signalREnabled())
      return 'Real-time updates disabled (signalRActive=false)';
    return this.signalRConnected()
      ? 'Real-time updates active'
      : 'Not connected: real-time updates unavailable';
  });

  /**
   * Returns true if connected to the production database.
   * Uses server-derived databaseType for unambiguous identification.
   */
  protected readonly isProductionDatabase = computed(() => {
    return this.info()?.databaseType === 'production';
  });

  /** The title is deliberately shown as text only; it is never treated as Markdown. */
  protected readonly previewPullRequestTitle = computed(() => {
    if (this.environmentType() !== 'preview') return null;

    const metadata = getPreviewPullRequestMetadata(
      this.info()?.prNumber,
      this.appConfig.config.previewPullRequestTitleBase64,
      null
    );

    // Do not show metadata detached from a valid preview pull request.
    return metadata.url ? metadata.title : null;
  });

  /** Whether the banner can be dismissed */
  readonly dismissible = input(false);

  /** Emitted when user dismisses the banner */
  readonly dismissed = output<void>();

  /** Emitted when user clicks on environment/database info */
  readonly infoClicked = output<void>();
}
